import {
  PRECISION_STANDARD,
  PRECISION_CURRENCY,
  PRECISION_PERCENTAGE_EXTENDED,
} from '../precision';
import { maskSsn, maskPhone, maskTaxId, wholeNumberWithCommas } from '../formatters';
import {
  formatDecimalFixed,
  formatDecimalNatural,
  formatPercentageFixed,
  formatPercentageNatural,
} from '../display/naturalDecimal';

// ---------------------------------------------------------------------------
// Generic registry factory
// ---------------------------------------------------------------------------

/**
 * Generic registry factory — string-keyed, type-safe, frozen after bootstrap.
 *
 * Used by:
 * - FormatRegistry (pure display transforms, keyed by formatKey string from JSON)
 * - AdapterRegistry (form-engine internal — field type → React component)
 *
 * NOT used by ActionRegistry (that lives in @packages/table-builder and has
 * its own decorator-based registration pattern).
 */
export interface Registry<T> {
  register(key: string, value: T): void;
  get(key: string): T | undefined;
  has(key: string): boolean;
  keys(): string[];
  freeze(): void;
}

export function createRegistry<T>(name: string): Registry<T> {
  const store = new Map<string, T>();
  let frozen = false;

  return {
    register(key: string, value: T): void {
      if (frozen) {
        throw new Error(
          `[${name}] Cannot register "${key}" — registry is frozen. ` +
            `All registrations must happen during module initialization.`,
        );
      }
      if (store.has(key)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[${name}] Overwriting existing key "${key}"`);
        }
      }
      store.set(key, value);
    },

    get(key: string): T | undefined {
      return store.get(key);
    },

    has(key: string): boolean {
      return store.has(key);
    },

    keys(): string[] {
      return Array.from(store.keys());
    },

    freeze(): void {
      frozen = true;
    },
  };
}

// ---------------------------------------------------------------------------
// FormatRegistry — precision-aware display formatters
// ---------------------------------------------------------------------------

/**
 * Options passed to precision-aware format functions.
 * The engine resolves these from the field's JSON metadata
 * (`displayPrecision`, `displayCurrency`, `displayLocale`).
 */
export interface FormatOptions {
  precision?: number;
  currency?: string;
  locale?: string;
  /** When true (default), omit trailing fractional zeros. */
  naturalPrecision?: boolean;
}

/**
 * A display-format function: takes a raw value + optional formatting options,
 * returns a human-readable display string.
 *
 * Distinct from `FormatterFn` (types/) which is `string → string`
 * for input masking. FormatFn handles display-time formatting with precision.
 */
export type FormatFn = (value: unknown, opts?: FormatOptions) => string;

export const FormatRegistry = createRegistry<FormatFn>('FormatRegistry');

// ---------------------------------------------------------------------------
// Built-in format registrations
// ---------------------------------------------------------------------------

FormatRegistry.register('CURRENCY', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_CURRENCY;
  const currency = opts?.currency ?? 'USD';
  const locale = opts?.locale ?? 'en-US';
  const naturalPrecision = opts?.naturalPrecision ?? true;
  const num = Number(value);
  if (naturalPrecision) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(num);
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(num);
});

FormatRegistry.register('NUMBER', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_STANDARD;
  const locale = opts?.locale ?? 'en-US';
  const naturalPrecision = opts?.naturalPrecision ?? true;
  const num = Number(value);
  return naturalPrecision
    ? formatDecimalNatural(num, precision, locale)
    : formatDecimalFixed(num, precision, locale);
});

FormatRegistry.register('PERCENT', (value, opts) => {
  const precision = opts?.precision ?? PRECISION_PERCENTAGE_EXTENDED;
  const naturalPrecision = opts?.naturalPrecision ?? true;
  const num = Number(value);
  return naturalPrecision
    ? formatPercentageNatural(num, precision)
    : formatPercentageFixed(num, precision);
});

FormatRegistry.register('COMMA_WHOLE_NUMBER', (value) => {
  return wholeNumberWithCommas(String(value));
});

// ---------------------------------------------------------------------------
// Identity / masking display formatters (precision-independent)
// ---------------------------------------------------------------------------

FormatRegistry.register('SSN_US', (value) => maskSsn(String(value)));
FormatRegistry.register('PHONE_US', (value) => maskPhone(String(value)));
FormatRegistry.register('TAX_ID_US', (value) => maskTaxId(String(value)));

FormatRegistry.register('DATE_MDY', (value) => {
  const s = String(value);
  const parts = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
});

FormatRegistry.register('IDENTITY', (value) => String(value ?? ''));
